import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, clearSession } from "../lib/api";

const AuthContext = createContext(null);

function normalizeUser(user) {
    if (!user) return user;
    return {
        ...user,
        warung_name: user.mitraProfile?.shopName || user.warung_name || user.shopName,
        owner_name: user.fullName || user.owner_name,
        balance: user.wallet?.balance ?? user.balance ?? 0,
    };
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/users/me");
            setUser(normalizeUser(data));
        } catch (e) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const expire = () => setUser(false);
        window.addEventListener("warungpay:session-expired", expire);
        fetchMe();
        return () => window.removeEventListener("warungpay:session-expired", expire);
    }, [fetchMe]);

    const storeTokens = (tokenData) => {
        const accessToken = tokenData.accessToken ?? tokenData.token;
        const refreshToken = tokenData.refreshToken;
        if (accessToken) {
            localStorage.setItem("warungpay_token", accessToken);
        }
        if (refreshToken) {
            localStorage.setItem("warungpay_refresh_token", refreshToken);
        }
    };

    const login = async (email, password) => {
        const normalizedEmail = String(email || "").trim().toLowerCase();
        try {
            // eslint-disable-next-line no-console
            console.debug('[auth] login attempt', normalizedEmail);
            const { data } = await api.post("/auth/login", { email: normalizedEmail, password });
            storeTokens(data);
            const token = data.accessToken ?? data.token;
            const { data: profile } = await api.get("/users/me", {
                headers: { Authorization: `Bearer ${token}` },
            });
            const normalized = normalizeUser(profile);
            setUser(normalized);
            return normalized;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[auth] login error', e?.response?.status, e?.response?.data || e?.message || e);
            throw e;
        }
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        storeTokens(data);
        const { data: profile } = await api.get("/users/me");
        const normalized = normalizeUser(profile);
        setUser(normalized);
        return normalized;
    };

    const logout = async (allSessions = false) => {
        try {
            const refreshToken = localStorage.getItem("warungpay_refresh_token");
            if (allSessions) {
                await api.post("/auth/logout-all");
            } else if (refreshToken) {

               
                await api.post("/auth/logout", { refreshToken });
            }
        } catch (error) {
            if (allSessions) throw error;
            // best-effort logout even if backend call fails
        }
        clearSession();
        setUser(false);
    };

    const refreshUser = fetchMe;

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

export { formatApiError };
